import {v4 as uuidv4} from "uuid";
import {http, HttpResponse} from "msw";
import {waitTime} from "@shared/mocks/index";
import {DatasetTypes, ElementTypes} from "@entities/widget-element";

// =============================================================================
// Мок дашборда демонстрационного объекта №1: набор виджетов и физически правдоподобная генерация
// показаний по модели MQTT-эмулятора станции (см. stationMock/MQTT-METRICS.md).
// =============================================================================

const HOUR_MS = 3_600_000;
const TEN_MIN_MS = 600_000;
const TWO_HOUR_MS = 7_200_000;

// ---------- Базовые генераторы значений ----------

function round(value: number, precision = 1): number {
    const p = Math.pow(10, precision);
    return Math.round(value * p) / p;
}

/** Автокоррелированный шум AR(1) в коридоре ±amplitude вокруг base (§3.2 MQTT-METRICS.md) */
function smoothedWalk(base: number, points: number, amplitude = 0.03, k = 0.35): number[] {
    const values: number[] = [];
    let s = Math.random() * 2 - 1;
    for (let i = 0; i < points; i++) {
        s = (1 - k) * s + k * (Math.random() * 2 - 1);
        values.push(base * (1 + amplitude * s));
    }
    return values;
}

/** Коэффициент часовой неравномерности притока, сумма по суткам = 24.00 (§2.6) */
const HOURLY_FACTORS = [
    0.45, 0.35, 0.30, 0.30, 0.35, 0.50, 0.85, 1.35,
    1.75, 1.55, 1.40, 1.25, 1.15, 1.10, 1.05, 1.05,
    1.10, 1.25, 1.40, 1.45, 1.35, 1.15, 0.90, 0.65,
];

/** Суточный профиль расхода вокруг среднечасового значения */
function diurnal(hourlyAverage: number, points: number, stepMs: number): number[] {
    const t0 = Date.now() - (points - 1) * stepMs;
    const values: number[] = [];
    let s = Math.random() * 2 - 1;
    for (let i = 0; i < points; i++) {
        const hour = new Date(t0 + i * stepMs).getHours();
        s = 0.8 * s + 0.2 * (Math.random() * 2 - 1);
        values.push(hourlyAverage * HOURLY_FACTORS[hour] * (1 + 0.08 * s));
    }
    return values;
}

/** Циклический процесс (аэрация/денитрификация, работа/пауза узла): чередование двух уровней */
function cyclePhases(valueEven: number, valueOdd: number, points: number, periodPoints: number, amplitude = 0.06): number[] {
    const values: number[] = [];
    let s = 0;
    for (let i = 0; i < points; i++) {
        const target = Math.floor(i / periodPoints) % 2 === 0 ? valueEven : valueOdd;
        s = 0.7 * s + 0.3 * (Math.random() * 2 - 1);
        values.push(target * (1 + amplitude * s));
    }
    return values;
}

/** Плавно спадающий тренд с насыщением — забивание фильтра между промывками (§5.4) */
function decliningTrend(start: number, totalDrop: number, points: number, noise = 0.008): number[] {
    return new Array(points).fill(0).map((_, i) => {
        const t = i / (points - 1);
        const drop = totalDrop * (1 - Math.exp(-3 * t));
        return (start - drop) * (1 + noise * (Math.random() * 2 - 1));
    });
}

function toEvents(values: number[], stepMs: number, precision = 1) {
    const t = Date.now();
    return values.map((value, i) => ({
        value: round(value, precision),
        timestamp: t - (values.length - 1 - i) * stepMs,
    }));
}

function meta(id: string, name: string, unit: string) {
    return {dataset_id: id, name, unit, updated_at: Date.now()};
}

function chartDataset(id: string, unit: string, name: string, values: number[], stepMs: number, precision = 1) {
    return {metadata: meta(id, name, unit), events: toEvents(values, stepMs, precision)};
}

function singleValueDataset(id: string, unit: string, name: string, value: number, precision = 2) {
    return {metadata: meta(id, name, unit), events: [{value: round(value, precision), timestamp: Date.now()}]};
}

/** DEVICE_STATE: events[0] — STATE (вкл/выкл), events[1..3] — фазные токи (мА), если устройство трёхфазное */
function deviceStateDataset(id: string, name: string, running: boolean, phaseCurrentsBase?: number[]) {
    const events = [{value: running ? 1 : 0, timestamp: Date.now()}];
    (phaseCurrentsBase ?? []).forEach(base => {
        const current = running ? smoothedWalk(base, 1, 0.02)[0] : 0;
        events.push({value: Math.round(current), timestamp: Date.now()});
    });
    return {metadata: meta(id, name, "MA"), events};
}

// ---------- Реестр датасетов и элементов ----------

const datasets: Record<string, () => any> = {};
const elementsDatasets: Record<string, Set<string>> = {};

/** Регистрирует «живой» датасет (значение пересчитывается при каждом запросе) и возвращает ссылку для элемента */
function metric(unit: string, name: string, factory: (id: string, unit: string, name: string) => any) {
    const id = uuidv4();
    datasets[id] = () => factory(id, unit, name);
    return {id, unit, name};
}

function el(x: number, y: number, w: number, h: number, elementType: ElementTypes, name: string,
            refs: { id: string; unit: string; name: string }[], elementMetadata: any) {
    const element_id = uuidv4();
    elementsDatasets[element_id] = new Set(refs.map(r => r.id));
    return {
        element_id, x, y, w, h,
        element_content: {name, element_type: elementType, datasets: refs},
        element_metadata: elementMetadata,
    };
}

function wdg(x: number, y: number, w: number, h: number, name: string, cols: number, rows: number, elements: any[]) {
    return {widget_id: uuidv4(), x, y, w, h, widget_content: {name, cols, rows, elements}};
}

// ---------- Метрики станции (Демонстрационный объект №1, Q_сут = 1000 м³/сут, см. stationMock/stations/chulki.yaml) ----------

const Q_HOURLY = 1000 / 24; // 41.7 м³/ч — среднечасовой приток

const flowInlet = metric("CUBIC_METER_PER_HOUR", "Расход сточных вод на входе",
    (id, unit, name) => chartDataset(id, unit, name, diurnal(Q_HOURLY, 24, HOUR_MS), HOUR_MS, 1));

const flowOutlet = metric("CUBIC_METER_PER_HOUR", "Расход сточных вод на выходе",
    (id, unit, name) => chartDataset(id, unit, name, diurnal(Q_HOURLY * 0.97, 24, HOUR_MS), HOUR_MS, 1));

const flowInletNow = metric("CUBIC_METER_PER_HOUR", "Расход на входе сейчас",
    (id, unit, name) => singleValueDataset(id, unit, name, diurnal(Q_HOURLY, 1, HOUR_MS)[0], 1));

const tempInlet = metric("C", "Температура сточных вод на входе",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(16, 1, 0.03)[0], 1));

const energyTotal = metric("KILOWATT_HOUR", "Общее энергопотребление станции",
    (id, unit, name) => singleValueDataset(id, unit, name, 245_830 + Math.random() * 40, 1));

const volumeToday = metric("CUBIC_METER", "Принято сточных вод за сутки",
    (id, unit, name) => singleValueDataset(id, unit, name, 1000 * (new Date().getHours() / 24) * (1 + 0.03 * (Math.random() * 2 - 1)), 1));

const activePowerA = metric("KILOWATT", "Активная мощность, фаза A",
    (id, unit, name) => chartDataset(id, unit, name, smoothedWalk(24, 24, 0.08), HOUR_MS, 2));
const activePowerB = metric("KILOWATT", "Активная мощность, фаза B",
    (id, unit, name) => chartDataset(id, unit, name, smoothedWalk(23, 24, 0.08), HOUR_MS, 2));
const activePowerC = metric("KILOWATT", "Активная мощность, фаза C",
    (id, unit, name) => chartDataset(id, unit, name, smoothedWalk(25, 24, 0.08), HOUR_MS, 2));

const blowerCollectorPressure = metric("MBAR", "Давление в коллекторе воздуходувного модуля №1",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(290, 1, 0.02)[0], 0));

const blower1State = metric("MA", "Воздуходувка №1 (20 кВт, УПП)",
    (id, unit, name) => deviceStateDataset(id, name, true, [36800, 36950, 36700]));

const surgeLevel = metric("METER", "Уровень в усреднителе",
    (id, unit, name) => chartDataset(id, unit, name, smoothedWalk(2.15, 24, 0.05), HOUR_MS, 2));

const surgeAlkalinity = metric("MILLIGRAM_PER_LITER", "Щёлочность в усреднителе",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(4.5, 1, 0.06)[0], 2));

const surgePhosphorus = metric("MILLIGRAM_PER_LITER", "Концентрация фосфатов в усреднителе",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(1.6, 1, 0.08)[0], 2));

const bioLine1Flow = metric("CUBIC_METER_PER_HOUR", "Линия биоочистки №1",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(Q_HOURLY / 4, 1, 0.05)[0], 1));
const bioLine2Flow = metric("CUBIC_METER_PER_HOUR", "Линия биоочистки №2",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(Q_HOURLY / 4, 1, 0.05)[0], 1));
const bioLine3Flow = metric("CUBIC_METER_PER_HOUR", "Линия биоочистки №3",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(Q_HOURLY / 4, 1, 0.05)[0], 1));
const bioLine4Flow = metric("CUBIC_METER_PER_HOUR", "Линия биоочистки №4",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(Q_HOURLY / 4, 1, 0.05)[0], 1));

const bioPh = metric("NONE", "pH, линия биоочистки №1",
    (id, unit, name) => chartDataset(id, unit, name, cyclePhases(6.95, 7.35, 24, 4, 0.015), TEN_MIN_MS, 2));
const bioOxygen = metric("MILLIGRAM_PER_LITER", "Растворённый кислород, линия №1",
    (id, unit, name) => chartDataset(id, unit, name, cyclePhases(2.4, 0.2, 24, 4, 0.12), TEN_MIN_MS, 2));
const bioNitrogen = metric("MILLIGRAM_PER_LITER", "Нитраты, линия №1",
    (id, unit, name) => chartDataset(id, unit, name, cyclePhases(11, 3, 24, 4, 0.08), TEN_MIN_MS, 2));

const recyclePumpState = metric("MA", "Насос возвратного рецикла, линия №1",
    (id, unit, name) => deviceStateDataset(id, name, true, [2750, 2820, 2690]));
const nitrateRecyclePumpState = metric("MA", "Насос нитратного рецикла, линия №1",
    (id, unit, name) => deviceStateDataset(id, name, false, [2900, 2960, 2870]));
const recyclePumpRunningTime = metric("HOUR", "Наработка насоса возвратного рецикла",
    (id, unit, name) => singleValueDataset(id, unit, name, 14320 + Math.random(), 1));

const dispenserHypochlorite = metric("NONE", "Дозатор гипохлорита натрия №1",
    (id, unit, name) => deviceStateDataset(id, name, true));
const dispenserAlkali = metric("NONE", "Дозатор щёлочи №1",
    (id, unit, name) => deviceStateDataset(id, name, false));
const dispenserPhosphate = metric("NONE", "Дозатор фосфатов №1",
    (id, unit, name) => deviceStateDataset(id, name, true));
const dispenserCoagulant = metric("NONE", "Дозатор коагулянта №1",
    (id, unit, name) => deviceStateDataset(id, name, true));

const filterMechPressure = metric("MBAR", "Давление после механического фильтра",
    (id, unit, name) => chartDataset(id, unit, name, decliningTrend(1180, 140, 12), TWO_HOUR_MS, 0));
const filterSorptionPressure = metric("MBAR", "Давление после сорбционного фильтра",
    (id, unit, name) => chartDataset(id, unit, name, decliningTrend(1040, 60, 12), TWO_HOUR_MS, 0));

const uvLampRunningTime = metric("HOUR", "Наработка УФ-лампы узла доочистки №1",
    (id, unit, name) => singleValueDataset(id, unit, name, 6120 + Math.random(), 1));
const uvLampState = metric("NONE", "УФ-лампа узла доочистки №1",
    (id, unit, name) => deviceStateDataset(id, name, true));

const dewatererState = metric("NONE", "Обезвоживатель №1",
    (id, unit, name) => deviceStateDataset(id, name, false));
const flocculantState = metric("NONE", "Флокулянтная станция",
    (id, unit, name) => deviceStateDataset(id, name, true));
const entrappedWaterLevel = metric("METER", "Уровень в накопителе дренажной воды",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(0.9, 1, 0.1)[0], 2));

const ambientTemp = metric("C", "Температура наружного воздуха",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(19.4, 1, 0.05)[0], 1));
const traceHeatingState = metric("MA", "Обогрев трубопроводов",
    (id, unit, name) => deviceStateDataset(id, name, false, [13000, 13200, 12900]));
const mechRoomTemp = metric("C", "Температура в отсеке мехочистки",
    (id, unit, name) => singleValueDataset(id, unit, name, smoothedWalk(25, 1, 0.04)[0], 1));

// ---------- Виджеты дашборда ----------

let dashboard = [
    wdg(0, 0, 12, 2, "Ключевые показатели станции", 4, 1, [
        el(0, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Расход на входе", [flowInletNow], {}),
        el(1, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Температура на входе", [tempInlet], {}),
        el(2, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Принято за сутки", [volumeToday], {}),
        el(3, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Энергопотребление", [energyTotal], {}),
    ]),

    wdg(0, 2, 8, 3, "Приток и отток сточных вод", 1, 1, [
        el(0, 0, 1, 1, ElementTypes.CHART, "Расход, вход/выход", [flowInlet, flowOutlet], {
            temporal_count: 24, precision: 1,
        }),
    ]),

    wdg(8, 2, 4, 3, "Электроснабжение станции", 1, 1, [
        el(0, 0, 1, 1, ElementTypes.CHART, "Активная мощность по фазам", [activePowerA, activePowerB, activePowerC], {
            temporal_count: 24, precision: 2,
        }),
    ]),

    wdg(0, 5, 4, 3, "Воздуходувный модуль №1", 2, 2, [
        el(0, 0, 1, 2, ElementTypes.GAUGE, "Давление в коллекторе", [blowerCollectorPressure], {
            threshold: {min: 0, max: 400}, settings: {min: 150, max: 320},
        }),
        el(1, 0, 1, 2, ElementTypes.DEVICE_STATE, "Воздуходувка №1", [blower1State], {}),
    ]),

    wdg(4, 5, 4, 3, "Усреднитель — уровень и качество", 3, 2, [
        el(0, 0, 2, 2, ElementTypes.CHART, "Уровень в усреднителе", [surgeLevel], {
            temporal_count: 24, precision: 2,
        }),
        el(2, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Щёлочность", [surgeAlkalinity], {}),
        el(2, 1, 1, 1, ElementTypes.SINGLE_VALUE, "Фосфаты", [surgePhosphorus], {}),
    ]),

    wdg(8, 5, 4, 3, "Расход по линиям биоочистки", 1, 1, [
        el(0, 0, 1, 1, ElementTypes.DIAGRAM, "Расход по линиям", [bioLine1Flow, bioLine2Flow, bioLine3Flow, bioLine4Flow], {
            dataset_type: DatasetTypes.CURRENT_VALUE,
        }),
    ]),

    wdg(0, 8, 6, 3, "Биологическая очистка — линия №1", 1, 1, [
        el(0, 0, 1, 1, ElementTypes.CHART, "pH, O₂, нитраты", [bioPh, bioOxygen, bioNitrogen], {
            temporal_count: 24, precision: 2,
        }),
    ]),

    wdg(6, 8, 6, 3, "Насосы рециркуляции, линия №1", 3, 1, [
        el(0, 0, 1, 1, ElementTypes.DEVICE_STATE, "Возвратный рецикл", [recyclePumpState], {}),
        el(1, 0, 1, 1, ElementTypes.DEVICE_STATE, "Нитратный рецикл", [nitrateRecyclePumpState], {}),
        el(2, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Наработка насоса", [recyclePumpRunningTime], {}),
    ]),

    wdg(0, 11, 4, 3, "Реагентное хозяйство", 2, 2, [
        el(0, 0, 1, 1, ElementTypes.DEVICE_STATE, "Гипохлорит натрия", [dispenserHypochlorite], {}),
        el(1, 0, 1, 1, ElementTypes.DEVICE_STATE, "Щёлочь", [dispenserAlkali], {}),
        el(0, 1, 1, 1, ElementTypes.DEVICE_STATE, "Фосфаты", [dispenserPhosphate], {}),
        el(1, 1, 1, 1, ElementTypes.DEVICE_STATE, "Коагулянт", [dispenserCoagulant], {}),
    ]),

    wdg(4, 11, 4, 3, "Доочистка и УФ-обеззараживание", 2, 2, [
        el(0, 0, 2, 1, ElementTypes.CHART, "Давление после фильтров", [filterMechPressure, filterSorptionPressure], {
            temporal_count: 12, precision: 0,
        }),
        el(0, 1, 1, 1, ElementTypes.SINGLE_VALUE, "Наработка УФ-лампы", [uvLampRunningTime], {}),
        el(1, 1, 1, 1, ElementTypes.DEVICE_STATE, "УФ-лампа", [uvLampState], {}),
    ]),

    wdg(8, 11, 4, 3, "Обработка осадка", 2, 2, [
        el(0, 0, 1, 1, ElementTypes.DEVICE_STATE, "Обезвоживатель №1", [dewatererState], {}),
        el(1, 0, 1, 1, ElementTypes.DEVICE_STATE, "Флокулянтная станция", [flocculantState], {}),
        el(0, 1, 2, 1, ElementTypes.GAUGE, "Уровень дренажной воды", [entrappedWaterLevel], {
            threshold: {min: 0, max: 2.5}, settings: {min: 0.6, max: 1.75},
        }),
    ]),

    wdg(0, 14, 6, 2, "Обогрев трубопроводов и климат", 3, 1, [
        el(0, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Наружный воздух", [ambientTemp], {}),
        el(1, 0, 1, 1, ElementTypes.DEVICE_STATE, "Обогрев трубопроводов", [traceHeatingState], {}),
        el(2, 0, 1, 1, ElementTypes.SINGLE_VALUE, "Отсек мехочистки", [mechRoomTemp], {}),
    ]),

    wdg(6, 14, 6, 2, "Забивание фильтра доочистки", 1, 1, [
        el(0, 0, 1, 1, ElementTypes.CHART, "Давление после мех. фильтра", [filterMechPressure], {
            temporal_count: 12, precision: 0,
        }),
    ]),
];

// ---------- HTTP-хендлеры (MSW) ----------

const apiUrl = "*/api/v2";
const dashboardUrl = apiUrl + "/dashboard";
const elementUrl = apiUrl + "/dashboard/widget_elements/:element_id/events";
const datasetUrl = apiUrl + "/dashboard/dataset/:dataset_id/preview";

const getDashboard = http.get(dashboardUrl, async () => {
    await waitTime(1000);

    return HttpResponse.json(dashboard);
});

const putDashboard = http.put(dashboardUrl, async ({request}) => {
    const db = await request.json();

    dashboard = (db as any).map((widget: any) => ({
        ...widget,
        widget_id: widget.widget_id || uuidv4(),
        widget_content: {
            ...widget.widget_content,
            name: widget.widget_content.name,
            elements: widget.widget_content.elements.map((child: any) => {
                const id = child.element_id || uuidv4();

                child.element_content.datasets.forEach((dataset: any) => {
                    if (!datasets[dataset.id]) {
                        datasets[dataset.id] = () => chartDataset(dataset.id, dataset.unit || "NONE", dataset.name,
                            smoothedWalk(50, 24, 0.15), HOUR_MS, 1);
                    }
                });
                elementsDatasets[id] = new Set(child.element_content.datasets.map((d: any) => d.id));

                return {
                    ...child,
                    element_id: id,
                };
            })
        }
    }));

    await waitTime(1000);
    return HttpResponse.json(dashboard);
});

const getElementDatasets = http.get(elementUrl, async ({params}) => {
    await waitTime(1000);

    const elementId = params["element_id"] as string;

    const res = Array.from(elementsDatasets[elementId] ?? []).map((id: string) => datasets[id]());

    return HttpResponse.json(res);
});

const getPreviewDataset = http.get(datasetUrl, async ({params}) => {
    await waitTime(1000);

    const datasetId = params["dataset_id"] as string;

    if (!datasets[datasetId]) {
        datasets[datasetId] = () => chartDataset(datasetId, "NONE", "Новый датасет", smoothedWalk(50, 24, 0.15), HOUR_MS, 1);
    }

    return HttpResponse.json(datasets[datasetId]());
});

export const dashboardHandlers = [
    getDashboard,
    putDashboard,
    getElementDatasets,
    getPreviewDataset,
];
